import React, { useState, FC, memo, useEffect } from 'react'
import moment, { Moment } from 'moment'
import { Card, Info, Title } from 'components/common'
import { RangePicker } from 'components/common/date-picker/range-picker'

type Props = {
  loading?: boolean
  alreadyContactRequiredDates?: string[]
  setContactRequiredDates?: React.Dispatch<React.SetStateAction<string[]>>
}

const colors = {
  newly: '#009688',
  already: '#03a9f4',
  delete: '#f44336',
  text: '#fff',
  nonActive: 'transparent',
}

const ContactRequiredSettings: FC<Props> = (props) => {
  const [selectedRange, setSelectedRange] = useState([])
  const { setContactRequiredDates, alreadyContactRequiredDates, loading } =
    props
  const alreadySelectedDates = new Set(alreadyContactRequiredDates)
  const [selectedDates, setSelectedDates] = useState<Map<string, string>>(
    new Map()
  )
  const [inquiryStatus, setInquiryStatus] = useState<Map<string, boolean>>(
    new Map()
  )
  // カレンダーの初期値をセットする処理
  useEffect(() => {
    if (!alreadyContactRequiredDates) return
    const initSelectedDates = new Map()
    alreadyContactRequiredDates.forEach((date) => {
      initSelectedDates.set(date, colors.already)
    })
    setSelectedDates(initSelectedDates)

    const initInquiryStatus = new Map()
    alreadySelectedDates.forEach((date) => {
      initInquiryStatus.set(date, true)
    })
    setInquiryStatus(initInquiryStatus)

    // お問い合わせステータスがtrueのMapのkey（日付）を格納した配列を生成
    const newContactRequiredDates = Array.from(initInquiryStatus.entries())
      .filter(([key, value]) => value)
      .map(([key]) => key)

    // お問い合わせステータス管理用stateを更新
    setContactRequiredDates(newContactRequiredDates)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyContactRequiredDates])

  /**
   * カレンダーで選択した範囲に応じてstateと背景色を更新する
   */
  const handleRangeSelect = (dates: [moment.Moment, moment.Moment] | null) => {
    if (!dates) return
    console.log(dates)

    // 背景色変更用のMap
    const newSelectedDates = new Map(selectedDates)
    // お問い合わせフラグ更新用のMap
    const newInquiryStatus = new Map(inquiryStatus)

    // 開始日
    const startDate = dates[0]
    // 終了日
    const endDate = dates[1]

    // 選択範囲内の日付のステータスと背景色を変更する処理
    const currentDate = startDate.clone()
    while (currentDate.isSameOrBefore(endDate)) {
      const dateStr = currentDate.format('YYYY-MM-DD')
      const isHighlighted = alreadySelectedDates.has(dateStr)
      const selectedColor = selectedDates.get(dateStr)

      if (selectedColor === colors.delete) {
        newSelectedDates.set(dateStr, colors.already)
        newInquiryStatus.set(dateStr, true)
      } else if (selectedColor === colors.newly) {
        newSelectedDates.delete(dateStr)
        newInquiryStatus.delete(dateStr)
      } else if (isHighlighted) {
        newSelectedDates.set(dateStr, colors.delete)
        newInquiryStatus.set(dateStr, false)
      } else {
        newSelectedDates.set(dateStr, colors.newly)
        newInquiryStatus.set(dateStr, true)
      }
      currentDate.add(1, 'days')
    }

    // 選択範囲の背景色を更新
    setSelectedDates(newSelectedDates)
    // 選択範囲のお問い合わせステータスを更新
    setInquiryStatus(newInquiryStatus)
    // 開始日と終了日が入力されたら、選択された範囲をリセット
    setSelectedRange([])

    // お問い合わせステータスがtrueのMapのkey（日付）を格納した配列を生成
    const newContactRequiredDates = Array.from(newInquiryStatus.entries())
      .filter(([key, value]) => value)
      .map(([key]) => key)

    // お問い合わせステータス管理用stateを更新
    setContactRequiredDates(newContactRequiredDates)
  }

  const onChange = (dates: [Moment, Moment] | null, dateStrings: string[]) => {
    if (!dates) {
      handleReset()
      return
    }
    setSelectedRange(dates)
    handleRangeSelect(dates)
  }

  const handleReset = () => {
    setSelectedRange([])
    setSelectedDates(new Map())
  }

  const dateRender = (current) => {
    const dateStr = current.format('YYYY-MM-DD')
    const startDate = selectedRange[0] ? moment(selectedRange[0]) : null
    const endDate = selectedRange[1] ? moment(selectedRange[1]) : null
    const isHighlighted = alreadySelectedDates.has(dateStr)
    const isSelected = selectedDates.has(dateStr)
    const selectedColor = selectedDates.get(dateStr)

    // リファクタリングするとカレンダーに日付が表示されなくなる
    let style = {}
    if (
      startDate &&
      endDate &&
      current.isBetween(startDate, endDate, null, '[]')
    ) {
      style = {
        backgroundColor: colors.newly,
        color: colors.text,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }
    }
    // 既に登録されている日付
    if (isHighlighted) {
      style = {
        backgroundColor: colors.already,
        color: colors.text,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }
    }
    if (isSelected) {
      style = {
        backgroundColor: selectedColor,
        color: colors.text,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }
    }

    return (
      <div className="ant-picker-cell-inner" style={style}>
        {current.date()}
      </div>
    )
  }

  return (
    <Card shadow loading={loading}>
      <Info title="有効な日付は利用者サイトからの予約が作成できなくなり、カレンダーには電話での問い合わせを促すアイコンが表示されるようになります。">
        <Title level={5} space="none">
          お問い合わせフラグの設定
        </Title>
      </Info>

      <dl className="py-2 mb-0">
        <dt>ステータス</dt>
        <dd className="grid grid-cols-3 justify-items-start">
          <div className="flex items-center">
            <div
              className="mr-2 w-5 h-5"
              style={{ backgroundColor: colors.already }}
            />
            <div>現在有効な日付</div>
          </div>
          <div className="flex items-center">
            <div
              className="mr-2 w-5 h-5"
              style={{ backgroundColor: colors.delete }}
            />
            <div>無効にする日付</div>
          </div>
          <div className="flex items-center">
            <div
              className="mr-2 w-5 h-5"
              style={{ backgroundColor: colors.newly }}
            />
            <div>新規で有効にする日付</div>
          </div>
        </dd>
      </dl>

      <RangePicker onChange={onChange} dateRender={dateRender} picker="date" />
      <div className="flex justify-center items-center bg-gray-100 h-96 mt-8">
        <p className="opacity-80">開始日または終了日を選択してください</p>
      </div>
    </Card>
  )
}

export default memo(ContactRequiredSettings)
